import { getSdk } from "@/composables/useSdk";
import { resolveApiBaseUrl } from "@/utils/env";

export interface ApiResponse<T = any> {
  data: T;
  message?: string;
  status: number;
  success: boolean;
  next_cursor?: string;
  prev_cursor?: string;
  per_page?: number;
  path?: string;
  meta?: any;
}

export interface RequestConfig {
  params?: Record<string, any>;
  headers?: Record<string, string>;
  skipAuth?: boolean;
  credentials?: RequestCredentials;
}

class ApiClient {
  private get baseURL(): string {
    return resolveApiBaseUrl();
  }

  /**
   * Yield a usable in-memory access token, hydrating the ccsdk session first
   * when needed. ccsdk uses an in-memory token provider, so on a hard reload
   * the access token is gone while the refresh token is still persisted. The
   * first authed request after reload would otherwise fire with no bearer and
   * 401. We restore the persisted session, then refresh to mint an access
   * token. ccsdk dedups concurrent refreshes (refreshSessionInFlight), so
   * parallel first requests share one single-use /auth/refresh call.
   */
  private async resolveAccessToken(sdk: ReturnType<typeof getSdk>): Promise<string | undefined> {
    const existing = sdk.getTokens()?.accessToken;
    if (existing) return existing;

    try {
      const restored = await sdk.restoreSession();
      if (restored?.accessToken) return restored.accessToken;

      // restoreSession loaded a refresh token but no access token (cookie-mode
      // web): exchange it for an access token.
      if (restored?.refreshToken || sdk.getTokens()?.refreshToken) {
        const refreshed = await sdk.refreshSession();
        if (refreshed?.accessToken) return refreshed.accessToken;
      }
    } catch {
      // No recoverable session — fall through unauthenticated; the request
      // will 401 and callers handle it.
    }

    return sdk.getTokens()?.accessToken;
  }

  private async request<T>(method: string, url: string, data?: any, config?: RequestConfig): Promise<ApiResponse<T>> {
    const sdk = getSdk();
    const token = config?.skipAuth
      ? sdk.getTokens()?.accessToken
      : await this.resolveAccessToken(sdk);

    const headers: HeadersInit = {
      'Accept': 'application/json',
      ...config?.headers
    };

    // Only set Content-Type if not FormData (browser sets it automatically with boundary)
    if (!(data instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    if (token && !config?.skipAuth) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // URL construction
    const baseURL = this.baseURL.endsWith('/') ? this.baseURL.slice(0, -1) : this.baseURL;
    const path = url.startsWith('/') ? url : '/' + url;
    let fullUrl = url.startsWith('http') ? url : `${baseURL}${path}`;

    if (config?.params) {
      const searchParams = new URLSearchParams();
      Object.entries(config.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      if (searchParams.toString()) {
        fullUrl += (fullUrl.includes('?') ? '&' : '?') + searchParams.toString();
      }
    }

    const options: RequestInit = {
      method,
      headers,
      credentials: config?.credentials,
    };

    if (data) {
      if (data instanceof FormData) {
        options.body = data;
      } else {
        options.body = JSON.stringify(data);
      }
    }

    try {
      const response = await fetch(fullUrl, options);

      let responseData: any;
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        responseData = await response.json();
      } else {
        responseData = await response.text();
        try {
          responseData = JSON.parse(responseData);
        } catch (e) {
          // ignore
        }
      }

      if (!response.ok) {
        // Throw object similar to axios error for compatibility
        throw {
          response: {
            data: responseData,
            status: response.status,
            message: responseData?.message
          },
          message: responseData?.message || 'Request failed'
        };
      }

      return {
        data: responseData?.data || responseData,
        message: responseData?.message,
        status: response.status,
        success: true,
        next_cursor:
          responseData?.next_cursor ??
          (typeof responseData?.nextCursor === "string"
            ? responseData.nextCursor
            : undefined),
        prev_cursor: responseData?.prev_cursor,
        per_page: responseData?.per_page,
        path: responseData?.path,
        meta: responseData?.meta,
      };
    } catch (error: any) {
      throw error;
    }
  }

  public get<T>(url: string, params?: any, config?: RequestConfig) {
    return this.request<T>('GET', url, undefined, { ...config, params });
  }

  public post<T>(url: string, data?: any, config?: RequestConfig & { params?: Record<string, any> }) {
    return this.request<T>('POST', url, data, config);
  }

  public put<T>(url: string, data?: any, config?: RequestConfig) {
    return this.request<T>('PUT', url, data, config);
  }

  public delete<T>(url: string, config?: RequestConfig) {
    return this.request<T>('DELETE', url, undefined, config);
  }
}

export default new ApiClient();
